/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect } from 'react';
import { Form, FormGroup, Label, Input } from 'reactstrap';
import * as default_data from '../config.js'
import { getToken } from '../AuthService.js';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, IconButton, Alert, Button } from '@mui/material';
import { IoClose } from "react-icons/io5";
import { ScrollableDialogContent } from '../Components/ScrollableDialogContent.js';

const AddPersona = ({ loadPersonas, personas, triggerAddPersona, setTriggerAddPersona }) => {

  const [loaded, setLoaded] = useState(false);
  var loaded_static = false;

  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [public_f, setPublic] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = event => {
    event.preventDefault();
    setIsLoading(true);
  
    var token = getToken()
    if(token === false) {
      return;
    }
  
    // Create a new FormData object
    const formData = new FormData();
    // Append the form data
    formData.append('name', name);
    formData.append('description', description);
    formData.append('prompt', prompt);
    formData.append('public', public_f);
    // Append the file
    if (file) {
      formData.append('file', file);
    }
  
    fetch(default_data.default_prompt_service_url + '/persona', {
      method: 'PUT',
      headers: {
        'x-access-tokens': `${token}`
      },
      body: formData
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while submitting.');
      })
      .then(data => {
        if(data.status != 200) {
          throw new Error(data.response);
        }
        setIsLoading(false);
        // Clear the form and display a success message
        setName('');
        setDescription('');
        setPrompt('')
        setPublic(0)
        setErrorMessage('');
        setFile(null); // Clear the file
  
        loadPersonas()
        setSuccessMessage("Your persona has been added!");
        const sleep = async (milliseconds) => {
          await new Promise(resolve => {
              return setTimeout(resolve, milliseconds)
          });
        };
        const run_sleep = async () => {
          closeModal()
          setSuccessMessage('')
        }
        run_sleep();
      })
      .catch(error => {
        setIsLoading(false);
        setErrorMessage(error.message);
      });
  };  

   // Add a new state variable for the uploaded file
   const [file, setFile] = useState(null);

   // Define the handleFileChange function
   const handleFileChange = (event) => {
     const file = event.target.files[0];
     // Check if the file is an image and its size is less than 250KB
     if (file.type.startsWith('image/') && file.size <= 250 * 1024) {
       setFile(file);
     } else {
       setErrorMessage('Invalid file. Please upload a JPG or PNG image less than 250KB.');
     }
   }; 

  function openModal() {
    setIsOpen(true);
  }

  function closeModal() {
    setTriggerAddPersona(0);
    setIsOpen(false);
  }

  const handlePublic = (event) => {
    if(public_f == true || public_f == 1) {
      setPublic(0)
    }
    else {
      setPublic(1)
    }
  };
  
  useEffect(() => {
    if (triggerAddPersona) {
      if (triggerAddPersona === 1) {
        setIsOpen(true)
      }
    }
  }, [triggerAddPersona]);

  useEffect(() => {

    if(loaded || loaded_static) {
      return;
    }

    setLoaded(true)
    loaded_static = true;

    }, [])

  // debug add dataset limitation

  return (
    <Dialog maxWidth='xl' fullWidth open={modalIsOpen} onClose={closeModal} aria-labelledby="form-dialog-title">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Add a Persona (advanced)
          <IconButton onClick={closeModal}>
            <IoClose />
          </IconButton>
        </Box>
      </DialogTitle>
      <ScrollableDialogContent modalIsOpen={modalIsOpen}>
        <Alert severity="info" sx={{mb: 3}}>
          NOTE: in this mode, you will type your entire system prompt instead of using Ask Sage to generate it for you based on questions answered.<br />
          Please make sure you abide by our terms and conditions. We will ban your account if you violate LLMs' terms and conditions.<br />
          Think of the "prompt" field below as the "system prompt".
        </Alert>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="name">Name* (70 characters maximum)</Label>
            <Input required="required" size="70" type="text" value={name} onChange={event => setName(event.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label for="description">Description* (500 characters maximum)</Label>
            <Input size="500" required="required" type="textarea" value={description} onChange={event => setDescription(event.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label for="prompt">System Prompt* (please abide by T&Cs)</Label>
            <Input style={{minHeight: '200px'}} placeholder="You are a software developer with decades of experience, your responsibility is to write code that is not only efficient and reliable but also secure and meets the highest security standards such as the NIST Cybersecurity Framework and NIST Risk Management Framework. You must pay close attention to various types of injections, such as SQL injections and XSS injections, and ensure that code is free from vulnerabilities. Proper static and dynamic code security scanning and code review are essential to ensuring the security of the code. You are able to understand the requirements of a project and create code that meets those requirements. You must also be able to debug and troubleshoot any issues that arise. You always review your own code to ensure that it meets the highest security standards. Your purpose is to help government teams drive outcomes by providing them with secure and reliable software solutions that comply with specific security standards and protect against potential threats." required="required" type="textarea" value={prompt} onChange={event => setPrompt(event.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label for="file">Upload a logo for your persona (JPG or PNG, 250KB maximum)</Label>
            <Input type="file" onChange={handleFileChange} />
          </FormGroup>        
          <FormGroup>
            <Label for="public_f">Public (visible to everyone)</Label>&nbsp;
            <Input type="checkbox" value="{public_f}" onChange={handlePublic} />
          </FormGroup>
          {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
          {successMessage && <p style={{ color: 'green' }}>{successMessage}</p>}
        </Form>
      </ScrollableDialogContent>
      <DialogActions>
        <Button onClick={closeModal} color="error">
          Cancel
        </Button>
        <Button type="submit" variant='contained' disabled={isLoading} onClick={handleSubmit}>
          {isLoading ? 'Loading...' : 'Submit'}
        </Button>
      </DialogActions>
    </ Dialog>
  );
}
export default AddPersona;
